/**
 * Typh sticky ATC — quiet mobile/tablet overlay.
 * Submits the existing product form. Does not change gallery or size pickers.
 */
if (!customElements.get('typh-sticky-atc')) {
  customElements.define(
    'typh-sticky-atc',
    class TyphStickyAtc extends HTMLElement {
      connectedCallback() {
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
          return;
        }
        if (this.inited) return;
        this.inited = true;

        this.source = document.getElementById(this.dataset.sourceButton);
        this.variantRoot = document.getElementById(this.dataset.variantRoot);
        this.submit = this.querySelector('[data-sticky-submit]');
        this.label = this.querySelector('[data-sticky-label]');
        this.sizeEl = this.querySelector('[data-sticky-size]');
        this.sepEl = this.querySelector('[data-sticky-sep]');
        this.priceEl = this.querySelector('[data-sticky-price]');
        this.sizeComplete = true;
        if (!this.source || !this.submit) return;

        this.update = this.update.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.syncMeta = this.syncMeta.bind(this);
        this.submit.addEventListener('click', this.onSubmit);
        window.addEventListener('scroll', this.update, { passive: true });
        window.addEventListener('resize', this.update);
        window.visualViewport?.addEventListener('resize', this.update);
        window.visualViewport?.addEventListener('scroll', this.update);
        this.observer = new IntersectionObserver(this.update, { threshold: 0 });
        this.observer.observe(this.source);

        if (this.variantRoot) {
          this.variantRoot.addEventListener('change', this.syncMeta);
          this.variantRoot.addEventListener('click', this.syncMeta);
        }

        const priceRoot = document.getElementById(`price-${this.dataset.section}`);
        if (priceRoot) {
          this.priceObserver = new MutationObserver(this.syncMeta);
          this.priceObserver.observe(priceRoot, { childList: true, subtree: true, characterData: true });
        }

        this.attrObserver = new MutationObserver(this.update);
        this.attrObserver.observe(this.source, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
        });
        this.classObserver = new MutationObserver(this.update);
        this.classObserver.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['class'],
        });
        const cart = document.querySelector('cart-drawer');
        if (cart) this.classObserver.observe(cart, { attributes: true, attributeFilter: ['class'] });

        this.syncMeta();
        this.update();
      }

      disconnectedCallback() {
        if (this.parentElement === document.body || !this.inited) return;
        this.submit?.removeEventListener('click', this.onSubmit);
        window.removeEventListener('scroll', this.update);
        window.removeEventListener('resize', this.update);
        window.visualViewport?.removeEventListener('resize', this.update);
        window.visualViewport?.removeEventListener('scroll', this.update);
        this.variantRoot?.removeEventListener('change', this.syncMeta);
        this.variantRoot?.removeEventListener('click', this.syncMeta);
        this.observer?.disconnect();
        this.attrObserver?.disconnect();
        this.classObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.setPagePad(false);
      }

      onSubmit(event) {
        event.preventDefault();
        if (!this.sizeComplete) {
          this.variantRoot?.scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
        if (this.source.disabled || this.source.getAttribute('aria-disabled') === 'true') return;
        this.source.click();
      }

      prefsOpen() {
        const prefs = document.querySelector('.shopify-pc__prefs, #shopify-pc__prefs');
        if (!prefs) return false;
        const style = window.getComputedStyle(prefs);
        return style.display !== 'none' && style.visibility !== 'hidden' && prefs.offsetHeight > 8;
      }

      isBlocked() {
        return Boolean(
          document.documentElement.classList.contains('typh-sf-open') ||
            document.querySelector('cart-drawer.drawer.active') ||
            document.querySelector('header-drawer details.menu-opening') ||
            document.querySelector('#Details-menu-drawer-container[open]') ||
            this.prefsOpen()
        );
      }

      safariOffset() {
        const vv = window.visualViewport;
        if (!vv) return 0;
        return Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      }

      sellingPrice() {
        const priceRoot = document.getElementById(`price-${this.dataset.section}`);
        if (priceRoot) {
          const saleWrap = priceRoot.querySelector('.price__sale');
          const saleVisible = saleWrap && window.getComputedStyle(saleWrap).display !== 'none';
          const item = saleVisible
            ? saleWrap.querySelector('.price-item--sale')
            : priceRoot.querySelector('.price__regular .price-item--regular');
          const cleaned = (item?.textContent || '').replace(/\s+/g, ' ').trim();
          if (cleaned) return cleaned;
        }
        return this.dataset.price || '';
      }

      selectedSizes() {
        const parts = [];
        let complete = true;
        const fieldsets = this.variantRoot?.querySelectorAll('fieldset.product-form__input') || [];
        if (!fieldsets.length) return { parts, complete: true };

        fieldsets.forEach((fieldset) => {
          const legend = (
            fieldset.querySelector('.typh-sf-legend__text') || fieldset.querySelector('legend')
          )?.textContent
            ?.replace(/size\s*&\s*fit/i, '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
          const checked = fieldset.querySelector('input:checked');
          if (!checked) {
            complete = false;
            return;
          }
          const value = checked.value.trim().toUpperCase();
          if (this.dataset.isSet === 'true') {
            if (legend?.includes('top')) parts.push(`TOP ${value}`);
            else if (legend?.includes('pant')) parts.push(`PANT ${value}`);
            else parts.push(value);
          } else {
            parts.push(`SIZE ${value}`);
          }
        });

        return { parts, complete };
      }

      syncMeta() {
        const { parts, complete } = this.selectedSizes();
        this.sizeComplete = complete;
        if (this.sizeEl) this.sizeEl.textContent = complete ? parts.join(' · ') : '';
        if (this.sepEl) this.sepEl.hidden = !(complete && parts.length);
        if (this.priceEl) this.priceEl.textContent = this.sellingPrice();

        const soldOut = this.source?.disabled || this.source?.getAttribute('aria-disabled') === 'true';
        if (this.label) {
          this.label.textContent = !complete
            ? this.dataset.labelSize
            : soldOut
              ? this.source.querySelector(':scope > span')?.textContent.trim() || this.dataset.labelAtc
              : this.dataset.labelAtc;
        }
        this.submit.disabled = Boolean(complete && soldOut);
        this.classList.toggle('is-select-size', !complete);
      }

      setPagePad(visible) {
        const height = visible
          ? Math.ceil(this.getBoundingClientRect().height + this.safariOffset())
          : 0;
        document.documentElement.style.setProperty('--typh-sticky-atc-pad', `${height}px`);
        document.documentElement.classList.toggle('typh-sticky-atc-visible', visible);
      }

      update() {
        if (!this.source) return;
        this.syncMeta();
        const rect = this.source.getBoundingClientRect();
        const scrolledPast = rect.bottom < 12;
        const visible = scrolledPast && !this.isBlocked();
        this.style.bottom = visible ? `${this.safariOffset()}px` : '0px';
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', visible ? 'false' : 'true');
        this.setPagePad(visible);
      }
    }
  );
}
